import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { GoogleLogin } from '@react-oauth/google';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Shield, Star, Repeat, ArrowLeft, ArrowRight, LogIn, UserPlus, UserX } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { checkAccount } from '../../services/authService';
import { getErrorMessage } from '../../utils/errorHandler';

const benefits = [
  { icon: Shield, text: 'Gestione sus citas en linea' },
  { icon: Star, text: 'Acumule puntos de fidelidad' },
  { icon: Repeat, text: 'Reagende mas rapido' },
];

const TABS = [
  { key: 'login', label: 'Iniciar sesion', icon: LogIn },
  { key: 'register', label: 'Crear cuenta', icon: UserPlus },
  { key: 'guest', label: 'Sin registro', icon: UserX },
];

const AuthGateStep = ({ onAuthenticated, onContinueWithContact, onBack }) => {
  const { login, register: authRegister, loginWithGoogle } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [existingContact, setExistingContact] = useState(null); // contacto con cuenta ya existente

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm();
  const password = watch('password');

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    reset();
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsSubmitting(true);
      setError('');
      await loginWithGoogle(credentialResponse.credential);
      onAuthenticated();
    } catch (err) {
      setError(err.message || getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onLoginSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setError('');
      await login({ email: formData.email, password: formData.password });
      onAuthenticated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRegisterSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setError('');
      await authRegister({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        birthday_month: formData.birthday_month || undefined,
        birthday_day: formData.birthday_day || undefined,
      });
      onAuthenticated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onGuestSubmit = async (formData) => {
    const contact = {
      name: formData.guest_name,
      email: formData.guest_email,
      phone: formData.guest_phone,
    };
    // check-account (opcional): si el correo/teléfono ya tiene cuenta, se le
    // ofrece iniciar sesión antes de crear la cita como invitada.
    setExistingContact(null);
    try {
      setCheckingAccount(true);
      const res = await checkAccount({ email: contact.email, phone: contact.phone });
      if (res.data?.exists) {
        setExistingContact(contact);
        return;
      }
    } catch (err) {
      // La verificación es opcional: si falla, se continúa como invitada.
    } finally {
      setCheckingAccount(false);
    }
    onContinueWithContact(contact);
  };

  const loginValidation = {
    email: {
      required: 'El email es requerido',
      pattern: { value: /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z]{2,4}$/, message: 'El email no es valido' },
    },
    password: {
      required: 'La contrasena es requerida',
      minLength: { value: 6, message: 'Minimo 6 caracteres' },
    },
  };

  const registerValidation = {
    name: { required: 'El nombre es requerido', minLength: { value: 3, message: 'Minimo 3 caracteres' } },
    email: loginValidation.email,
    phone: { required: 'El telefono es requerido', pattern: { value: /^[0-9]+$/, message: 'Solo numeros' } },
    password: { required: 'La contrasena es requerida', minLength: { value: 8, message: 'Minimo 8 caracteres' } },
    password_confirmation: {
      required: 'Debe confirmar la contrasena',
      validate: (value) => value === password || 'Las contrasenas no coinciden',
    },
  };

  const guestValidation = {
    guest_name: { required: 'El nombre es requerido', minLength: { value: 3, message: 'Minimo 3 caracteres' } },
    guest_email: {
      required: 'El correo es requerido',
      pattern: { value: /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z]{2,4}$/, message: 'El correo no es valido' },
    },
    guest_phone: { required: 'El telefono es requerido', pattern: { value: /^[0-9]+$/, message: 'Solo numeros' } },
  };

  return (
    <Row className="justify-content-center">
      <Col xs={12} sm={6}>
      <h4 className="h5 fw-semibold text-center mb-3" style={{ color: '#333' }}>
        Datos de la cuenta
      </h4>

      {/* Benefits banner */}
      <div
        className="d-flex align-items-center gap-3 p-2 mb-3 rounded-3"
        style={{ backgroundColor: '#FFF0F5', border: '1px solid #FCE4EC', overflowX: 'auto' }}
      >
        {benefits.map((b, i) => (
          <div key={i} className="d-flex align-items-center gap-1 flex-shrink-0">
            <b.icon size={14} style={{ color: '#EF60A3' }} />
            <span style={{ fontSize: '0.7rem', color: '#555', whiteSpace: 'nowrap' }}>{b.text}</span>
          </div>
        ))}
      </div>

      {/* Tab buttons */}
      <div className="d-flex gap-1 mb-3">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => switchTab(tab.key)}
              className="btn btn-sm flex-grow-1 d-inline-flex align-items-center justify-content-center gap-1"
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '8px 6px',
                borderRadius: 10,
                backgroundColor: isActive ? '#EF60A3' : '#fff',
                color: isActive ? '#fff' : '#EF60A3',
                border: '1.5px solid #EF60A3',
                transition: 'all 0.2s',
              }}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

      {/* Login tab */}
      {activeTab === 'login' && (
        <Card style={{ border: '1px solid #FCE4EC', borderRadius: 12 }}>
          <Card.Body className="p-3">
            {/* Google Sign-In */}
            <div className="d-flex justify-content-center mb-3">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Error al iniciar sesion con Google')}
                text="continue_with"
                shape="rectangular"
                width="300"
                locale="es"
              />
            </div>
            <div className="d-flex align-items-center gap-2 mb-3">
              <hr className="flex-grow-1 m-0" />
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>o con su correo</span>
              <hr className="flex-grow-1 m-0" />
            </div>

            <Form onSubmit={handleSubmit(onLoginSubmit)}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Correo electronico</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="Ingrese su correo"
                  isInvalid={!!errors?.email}
                  {...register('email', loginValidation.email)}
                />
                <Form.Control.Feedback type="invalid">{errors?.email?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">Contrasena</Form.Label>
                <Form.Control
                  size="sm"
                  type="password"
                  placeholder="Ingrese su contrasena"
                  isInvalid={!!errors?.password}
                  {...register('password', loginValidation.password)}
                />
                <Form.Control.Feedback type="invalid">{errors?.password?.message}</Form.Control.Feedback>
              </Form.Group>

              <Button
                type="submit"
                className="w-100"
                disabled={isSubmitting}
                style={{ backgroundColor: '#EF60A3', borderColor: '#EF60A3', borderRadius: 20 }}
              >
                {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Iniciar Sesion'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Register tab */}
      {activeTab === 'register' && (
        <Card style={{ border: '1px solid #FCE4EC', borderRadius: 12 }}>
          <Card.Body className="p-3">
            <Form onSubmit={handleSubmit(onRegisterSubmit)}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Nombre completo</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Ingrese su nombre"
                  isInvalid={!!errors?.name}
                  {...register('name', registerValidation.name)}
                />
                <Form.Control.Feedback type="invalid">{errors?.name?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Correo electronico</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="Ingrese su correo"
                  isInvalid={!!errors?.email}
                  {...register('email', registerValidation.email)}
                />
                <Form.Control.Feedback type="invalid">{errors?.email?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Telefono</Form.Label>
                <Form.Control
                  size="sm"
                  type="tel"
                  placeholder="Ingrese su telefono"
                  isInvalid={!!errors?.phone}
                  {...register('phone', registerValidation.phone)}
                />
                <Form.Control.Feedback type="invalid">{errors?.phone?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Contrasena</Form.Label>
                <Form.Control
                  size="sm"
                  type="password"
                  placeholder="Minimo 8 caracteres"
                  isInvalid={!!errors?.password}
                  {...register('password', registerValidation.password)}
                />
                <Form.Control.Feedback type="invalid">{errors?.password?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">Confirmar contrasena</Form.Label>
                <Form.Control
                  size="sm"
                  type="password"
                  placeholder="Repita su contrasena"
                  isInvalid={!!errors?.password_confirmation}
                  {...register('password_confirmation', registerValidation.password_confirmation)}
                />
                <Form.Control.Feedback type="invalid">{errors?.password_confirmation?.message}</Form.Control.Feedback>
              </Form.Group>

              {/* Birthday selector */}
              <div className="mb-3 p-2 rounded-3" style={{ backgroundColor: '#FFF8FA', border: '1px solid #FCE4EC' }}>
                <p className="small mb-2" style={{ color: '#EF60A3', fontSize: '0.75rem' }}>
                  🎂 Recibe ofertas increibles en tu dia de cumpleanos
                </p>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    style={{ flex: 1 }}
                    {...register('birthday_month')}
                  >
                    <option value="">Mes</option>
                    {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    style={{ flex: 1 }}
                    {...register('birthday_day')}
                  >
                    <option value="">Dia</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <Button
                type="submit"
                className="w-100"
                disabled={isSubmitting}
                style={{ backgroundColor: '#EF60A3', borderColor: '#EF60A3', borderRadius: 20 }}
              >
                {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Crear Cuenta'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Guest tab */}
      {activeTab === 'guest' && (
        <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}>
          <Card.Body className="p-3">
            <p className="small fw-semibold mb-3" style={{ color: '#333' }}>
              Datos de contacto
            </p>
            <Form onSubmit={handleSubmit(onGuestSubmit)}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Nombre completo</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Ingrese su nombre"
                  isInvalid={!!errors.guest_name}
                  {...register('guest_name', guestValidation.guest_name)}
                />
                <Form.Control.Feedback type="invalid">{errors.guest_name?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-medium">Correo electronico</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="Ingrese su correo"
                  isInvalid={!!errors.guest_email}
                  {...register('guest_email', guestValidation.guest_email)}
                />
                <Form.Control.Feedback type="invalid">{errors.guest_email?.message}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">Telefono</Form.Label>
                <Form.Control
                  size="sm"
                  type="tel"
                  placeholder="Ingrese su telefono"
                  isInvalid={!!errors.guest_phone}
                  {...register('guest_phone', guestValidation.guest_phone)}
                />
                <Form.Control.Feedback type="invalid">{errors.guest_phone?.message}</Form.Control.Feedback>
              </Form.Group>

              {existingContact && (
                <Alert variant="warning" className="py-2 small">
                  <p className="mb-2">
                    Ya existe una cuenta con este correo o teléfono. Inicia sesión para
                    que la cita quede en tu cuenta (y acumules sellos), o continúa como
                    invitada.
                  </p>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      style={{ backgroundColor: '#EF60A3', borderColor: '#EF60A3', borderRadius: 20 }}
                      onClick={() => { setExistingContact(null); switchTab('login'); }}
                    >
                      <LogIn size={12} className="me-1" /> Iniciar sesión
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      style={{ borderRadius: 20 }}
                      onClick={() => onContinueWithContact(existingContact)}
                    >
                      Continuar como invitada
                    </Button>
                  </div>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={checkingAccount}
                className="w-100 d-inline-flex align-items-center justify-content-center gap-2"
                style={{
                  backgroundColor: '#EF60A3',
                  borderColor: '#EF60A3',
                  borderRadius: 20,
                }}
              >
                {checkingAccount
                  ? <><Spinner animation="border" size="sm" /> Verificando…</>
                  : <>Continuar <ArrowRight size={14} /></>}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Back button */}
      <div className="mt-4">
        <Button variant="outline-secondary" onClick={onBack}>
          <ArrowLeft size={14} className="me-1" /> Atras
        </Button>
      </div>
      </Col>
    </Row>
  );
};

export default AuthGateStep;
